import { useEffect, useState } from "react";
import { Shuffle } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import useApiMethods from "../../lib/useApiMethods";
import { ASSIGNMENT_SETTINGS_URL, ASSIGNMENT_SUMMARY_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { Stat, inputCls, minutesLabel, METHOD_LABELS } from "../../components/assignment/shared";
import dayjs from "dayjs";

/**
 * Reports -> Lead Assignment. Is assignment fast, complete and fair?
 * customer/assignment/summary/. Measures the engine (speed, coverage,
 * fairness) - not sales results, which depend on the rep and the lead.
 */
export default function LeadAssignmentReport() {
    const { fetch } = useApiMethods();
    const [range, setRange] = useState({
        date_from: dayjs().subtract(29, "day").format("YYYY-MM-DD"),
        date_to: dayjs().format("YYYY-MM-DD"),
        team_id: "",
    });
    const [teams, setTeams] = useState([]);
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        fetch(ASSIGNMENT_SETTINGS_URL).then(res => { if (res?.status === "success") setTeams(res.teams || []); });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        setData(null);
        fetch(ASSIGNMENT_SUMMARY_URL, range).then(res => {
            if (res?.status === "success") { setData(res); setError(""); }
            else setError(res?.message || "Could not load the report");
        });
    }, [range]); // eslint-disable-line react-hooks/exhaustive-deps

    const t = data?.tiles || {};
    const days = data ? dailySeries(data.range.from, data.range.to, data.daily_auto) : [];

    return (
        <ContainerCard>
            <div className="p-4 space-y-5 overflow-auto">
                <div className="flex flex-wrap items-end justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <Shuffle className="h-5 w-5 text-primary" />
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Lead Assignment</h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400">How fast leads get an owner, how evenly, and what was left waiting.</p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <input type="date" value={range.date_from} aria-label="From"
                            onChange={e => setRange(r => ({ ...r, date_from: e.target.value }))} className={cn(inputCls, "w-36")} />
                        <input type="date" value={range.date_to} aria-label="To"
                            onChange={e => setRange(r => ({ ...r, date_to: e.target.value }))} className={cn(inputCls, "w-36")} />
                        <select value={range.team_id} onChange={e => setRange(r => ({ ...r, team_id: e.target.value }))} className={cn(inputCls, "w-36")}>
                            <option value="">All teams</option>
                            {teams.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
                        </select>
                    </div>
                </div>

                {error && <p className="text-xs text-gray-500">{error}</p>}
                {!data && !error && <p className="text-xs text-gray-400">Loading…</p>}

                {data && (
                    <>
                        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                            <Stat label="Leads given an owner" value={t.assigned} />
                            <Stat label="Automatic" value={t.assigned ? `${Math.round(100 * t.auto / t.assigned)}%` : "—"}
                                hint={`${t.auto} automatic · ${t.manual} by hand`} />
                            <Stat label="Unassigned right now" value={t.unassigned_now} tone={t.unassigned_now ? "warn" : undefined} />
                            <Stat label="Time to an owner (median)" value={minutesLabel(t.median_minutes_to_assign)}
                                hint={t.p90_minutes_to_assign != null ? `9 in 10 within ${minutesLabel(t.p90_minutes_to_assign)}` : "Automatic assignments only"} />
                            <Stat label="Fairness (most ÷ fewest)" value={t.fairness_ratio ?? "—"}
                                tone={t.fairness_ratio > 1.5 ? "warn" : undefined} hint="1.0 is perfectly even" />
                        </div>

                        <section>
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Leads assigned automatically, per day</h3>
                            <DayBars days={days} />
                        </section>

                        <div className="grid lg:grid-cols-2 gap-5">
                            <section>
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">By rep</h3>
                                <BarTable rows={data.by_rep} label={r => r.name} value={r => r.total}
                                    extra={r => `${r.auto} auto · ${r.manual} by hand`} empty="No leads assigned in this period" />
                            </section>
                            <section>
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Why leads were left unassigned</h3>
                                <BarTable rows={data.reasons} label={r => r.label} value={r => r.n} empty="Nothing was left unassigned" />
                            </section>
                            <section>
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">How owners were set</h3>
                                <BarTable rows={Object.entries(data.by_method).map(([k, n]) => ({ k, n }))}
                                    label={r => METHOD_LABELS[r.k] || r.k} value={r => r.n} empty="No owner changes" />
                            </section>
                            <section>
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Rules</h3>
                                <SimpleTable cols={["Rule", "Matched", "Assigned", "Fell back"]}
                                    rows={data.rules.map(r => [r.rule__name || `Rule #${r.rule_id}`, r.total, r.assigned, r.fallback])}
                                    empty="No rule decided a lead in this period" />
                            </section>
                        </div>

                        <section>
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">New leads by source</h3>
                            <SimpleTable cols={["Source", "New leads", "Assigned automatically", "Still unowned"]}
                                rows={data.sources.map(s => [s.source, s.leads, `${s.auto} (${s.leads ? Math.round(100 * s.auto / s.leads) : 0}%)`, s.unowned])}
                                empty="No new leads in this period" />
                        </section>
                    </>
                )}
            </div>
        </ContainerCard>
    );
}

function dailySeries(from, to, counts) {
    const out = [];
    for (let d = dayjs(from); !d.isAfter(dayjs(to)); d = d.add(1, "day")) {
        const key = d.format("YYYY-MM-DD");
        out.push({ key, label: d.format("D MMM"), n: counts[key] || 0 });
    }
    return out;
}

function DayBars({ days }) {
    const max = Math.max(1, ...days.map(d => d.n));
    if (!days.some(d => d.n)) return <p className="text-xs text-gray-400 py-4">No automatic assignments in this period.</p>;
    return (
        <div className="mt-2">
            <div className="flex items-end gap-0.5 h-32 border-b border-gray-200 dark:border-gray-700" role="img"
                aria-label="Leads assigned automatically per day">
                {days.map(d => (
                    <div key={d.key} className="flex-1 h-full flex items-end group relative" title={`${d.label}: ${d.n}`}>
                        <div className="w-full bg-primary rounded-t-[4px] group-hover:opacity-80"
                            style={{ height: `${(d.n / max) * 100}%`, minHeight: d.n ? 2 : 0 }} />
                    </div>
                ))}
            </div>
            <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                <span>{days[0].label}</span><span>max {max} a day</span><span>{days[days.length - 1].label}</span>
            </div>
        </div>
    );
}

function BarTable({ rows, label, value, extra, empty }) {
    if (!rows?.length) return <p className="text-xs text-gray-400">{empty}</p>;
    const max = Math.max(1, ...rows.map(value));
    return (
        <div className="space-y-1.5">
            {rows.map((r, i) => (
                <div key={i} className="grid grid-cols-[minmax(6rem,10rem)_1fr_auto] items-center gap-2 text-xs" title={extra ? extra(r) : undefined}>
                    <span className="truncate text-gray-700 dark:text-gray-200">{label(r)}</span>
                    <div className="h-3 bg-gray-100 dark:bg-gray-700/50 rounded">
                        <div className="h-3 bg-primary rounded-r-[4px]" style={{ width: `${(value(r) / max) * 100}%` }} />
                    </div>
                    <span className="tabular-nums text-gray-900 dark:text-white w-10 text-right">{value(r)}</span>
                </div>
            ))}
        </div>
    );
}

function SimpleTable({ cols, rows, empty }) {
    if (!rows.length) return <p className="text-xs text-gray-400">{empty}</p>;
    return (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="min-w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-700/40 text-gray-500">
                    <tr>{cols.map(c => <th key={c} className="px-3 py-2 text-left font-medium">{c}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {rows.map((r, i) => <tr key={i}>{r.map((c, j) =>
                        <td key={j} className={cn("px-3 py-2", j ? "tabular-nums text-gray-700 dark:text-gray-200" : "text-gray-800 dark:text-gray-100")}>{c}</td>)}</tr>)}
                </tbody>
            </table>
        </div>
    );
}
